// Client-side image downscaling + re-encoding, applied before ANY upload to
// Firebase Storage (js/services/firebase-auth.js: uploadWelcomeImage,
// uploadProfilePhoto).
//
// Why: welcome-page images are served full-size to every spectator of a public
// tournament, so an untouched 8 MB phone photo is paid for once per viewer in
// Storage egress — by far the largest projected running cost of the app. A
// 1600px WebP of the same photo is ~200-300 KB, a ~25x cut in both stored bytes
// and egress, with no visible difference on the page.
//
// The pure helpers (planResize/pickOutputType/renameForType) carry the decisions
// so they can be unit-tested in node; only resizeImageFile() touches the DOM.

// Per-surface tuning. maxBytes mirrors the ceiling in storage.rules — the rule
// is the real enforcement, this is the friendly early error.
export const IMAGE_PRESETS = {
    // Welcome-page banners/photos: rendered at most a page-width wide.
    welcome: { maxEdge: 1600, quality: 0.82, maxBytes: 10 * 1024 * 1024 },
    // Avatars: never rendered larger than a small circle.
    avatar: { maxEdge: 512, quality: 0.85, maxBytes: 10 * 1024 * 1024 }
};

// Formats we must not run through a canvas: SVG is already tiny and would be
// rasterized (losing scalability), GIF would lose its animation.
const PASSTHROUGH_TYPES = ['image/svg+xml', 'image/gif'];

// Source formats that can carry transparency — matters only when we have to
// fall back to JPEG, which cannot.
const ALPHA_TYPES = ['image/png', 'image/webp', 'image/avif'];

// True for files that must be uploaded byte-for-byte as they are.
export function isPassthrough(type) {
    return PASSTHROUGH_TYPES.includes(String(type || '').toLowerCase());
}

export function mayHaveAlpha(type) {
    return ALPHA_TYPES.includes(String(type || '').toLowerCase());
}

// Fit (width x height) inside a maxEdge box, preserving aspect ratio. Never
// upscales: a smaller image keeps its dimensions and is only re-encoded.
export function planResize(width, height, maxEdge) {
    const w = Math.max(1, Math.round(width || 0));
    const h = Math.max(1, Math.round(height || 0));
    const longest = Math.max(w, h);
    if (!maxEdge || longest <= maxEdge) return { width: w, height: h, scaled: false };
    const ratio = maxEdge / longest;
    return {
        width: Math.max(1, Math.round(w * ratio)),
        height: Math.max(1, Math.round(h * ratio)),
        scaled: true
    };
}

// Which format to encode to. WebP wherever the browser can encode it (~30%
// smaller than JPEG at equal quality and keeps alpha); JPEG otherwise.
// Returns null for formats that must pass through untouched.
export function pickOutputType(sourceType, canEncodeWebp) {
    if (isPassthrough(sourceType)) return null;
    return canEncodeWebp ? 'image/webp' : 'image/jpeg';
}

// Give the stored object an extension matching what we actually encoded, so the
// filename in Storage doesn't lie (a .png object holding WebP bytes).
export function renameForType(name, mimeType) {
    const base = String(name || 'image').replace(/\.[^.\s]*$/, '') || 'image';
    const ext = mimeType === 'image/webp' ? 'webp' : mimeType === 'image/jpeg' ? 'jpg' : '';
    return ext ? `${base}.${ext}` : String(name || 'image');
}

// ---- Browser-only from here down -------------------------------------------

let webpSupport = null;
function canEncodeWebp() {
    if (webpSupport !== null) return webpSupport;
    try {
        const c = document.createElement('canvas');
        c.width = 1; c.height = 1;
        webpSupport = c.toDataURL('image/webp').indexOf('data:image/webp') === 0;
    } catch (e) {
        webpSupport = false;
    }
    return webpSupport;
}

// Decode to something drawable. createImageBitmap is preferred because
// `imageOrientation: 'from-image'` applies the EXIF rotation phone photos carry
// — without it, portrait shots would be re-encoded sideways.
async function decodeImage(file) {
    if (typeof createImageBitmap === 'function') {
        try {
            return { source: await createImageBitmap(file, { imageOrientation: 'from-image' }), release: (s) => s.close && s.close() };
        } catch (e) { /* option unsupported or decode failed — try below */ }
        try {
            return { source: await createImageBitmap(file), release: (s) => s.close && s.close() };
        } catch (e) { /* fall through to the <img> path */ }
    }
    const url = URL.createObjectURL(file);
    try {
        const img = await new Promise((resolve, reject) => {
            const el = new Image();
            el.onload = () => resolve(el);
            el.onerror = () => reject(new Error('Could not decode image'));
            el.src = url;
        });
        return { source: img, release: () => URL.revokeObjectURL(url) };
    } catch (e) {
        URL.revokeObjectURL(url);
        throw e;
    }
}

function canvasToBlob(canvas, type, quality) {
    return new Promise((resolve) => {
        try {
            canvas.toBlob((blob) => resolve(blob), type, quality);
        } catch (e) {
            resolve(null);
        }
    });
}

// Downscale + re-encode `file` per `preset`. Always resolves to something
// uploadable: on any decode/encode failure it returns the ORIGINAL file, so a
// browser quirk can never block an arbiter from adding a picture. The caller
// still enforces the byte ceiling.
export async function resizeImageFile(file, preset = IMAGE_PRESETS.welcome) {
    if (!file) throw new Error('No file provided');
    if (isPassthrough(file.type)) return file;

    const outType = pickOutputType(file.type, canEncodeWebp());
    if (!outType) return file;

    let decoded = null;
    try {
        decoded = await decodeImage(file);
        const src = decoded.source;
        const plan = planResize(src.width, src.height, preset.maxEdge);

        const canvas = document.createElement('canvas');
        canvas.width = plan.width;
        canvas.height = plan.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return file;
        // JPEG has no alpha: a transparent PNG would otherwise composite onto
        // black. White matches the page background far better.
        if (outType === 'image/jpeg' && mayHaveAlpha(file.type)) {
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, plan.width, plan.height);
        }
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(src, 0, 0, plan.width, plan.height);

        const blob = await canvasToBlob(canvas, outType, preset.quality);
        // Keep whichever is smaller. An already-optimized small image can come
        // out of a re-encode LARGER, and there's no point paying for that.
        if (!blob || blob.size >= file.size) return file;

        return new File([blob], renameForType(file.name, blob.type), {
            type: blob.type,
            lastModified: Date.now()
        });
    } catch (e) {
        console.warn('Image resize failed, uploading original:', e);
        return file;
    } finally {
        if (decoded) {
            try { decoded.release(decoded.source); } catch (e) { /* nothing to do */ }
        }
    }
}
